import { FunctionComponent, useState, useMemo, useEffect } from "react";

import { Box } from "@mui/material";

import { useFilterBoxStateContext } from "./FilterBoxStateContext";
import { BaseFilterInput } from "./types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardMenuItem } from "@/dashboard-design-system/MenuItem";
import { DashboardSelect } from "@/dashboard-design-system/Select";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ChevronLeft, ChevronRight } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

interface FilterTypeSelectorProps {
  filterConfigId?: string;
  onTypeChange: (newConfigId: string) => void;
  defaultOpen?: boolean;
  onDropdownOpened?: () => void;
  onCategorySelected?: (params: { category: string; opensSubmenu: boolean }) => void;
  onSubmenuItemSelected?: (params: { category: string; filterType: string }) => void;
}

export const FilterTypeSelector: FunctionComponent<FilterTypeSelectorProps> = ({
  filterConfigId,
  onTypeChange,
  defaultOpen,
  onDropdownOpened,
  onCategorySelected,
  onSubmenuItemSelected,
}) => {
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(defaultOpen);
  const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const { t } = useTranslate();
  const { registry, filterVisibility } = useFilterBoxStateContext<BaseFilterInput>();

  useEffect(() => {
    if (defaultOpen) {
      onDropdownOpened?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fire once for the initial mount-open state only
  }, []);

  const allOptions = registry.getAll();

  const availableOptions = allOptions.filter((option) => {
    if (filterVisibility) {
      return filterVisibility(option);
    }
    return true;
  });

  const getGroupLabel = (groupName: string): string => {
    return (
      availableOptions.find((option) => option.selectGroup === groupName)?.selectGroupLabel ??
      t("dashboard", `ContactFiltering.groups.${groupName}` as never)
    );
  };

  const { groupedFilters, orderedMenuItems } = useMemo(() => {
    const grouped: Record<string, typeof availableOptions> = {};
    const menuItems: Array<{
      type: "filter" | "group";
      config?: (typeof availableOptions)[0];
      groupName?: string;
      icon?: (typeof availableOptions)[0]["icon"];
    }> = [];
    const seenGroups = new Set<string>();

    availableOptions.forEach((filterConfig) => {
      if (filterConfig.selectGroup) {
        if (!grouped[filterConfig.selectGroup]) {
          grouped[filterConfig.selectGroup] = [];
        }
        grouped[filterConfig.selectGroup].push(filterConfig);

        if (!seenGroups.has(filterConfig.selectGroup)) {
          seenGroups.add(filterConfig.selectGroup);
          menuItems.push({
            type: "group",
            groupName: filterConfig.selectGroup,
            icon: filterConfig.icon,
          });
        }
      } else {
        menuItems.push({
          type: "filter",
          config: filterConfig,
        });
      }
    });

    return { groupedFilters: grouped, orderedMenuItems: menuItems };
  }, [availableOptions]);

  const handleGroupClick = (groupName: string) => {
    setSelectedGroup(groupName);
    setIsSubMenuOpen(true);
    setIsMainMenuOpen(false);
    onCategorySelected?.({ category: groupName, opensSubmenu: true });
  };

  const handleBackClick = () => {
    setIsSubMenuOpen(false);
    setSelectedGroup(null);
    setIsMainMenuOpen(true);
  };

  const handleTypeChange = (newType: string) => {
    setIsSubMenuOpen(false);
    setIsMainMenuOpen(false);
    setSelectedGroup(null);
    onTypeChange(newType);
  };

  const renderValue = (value: unknown) => {
    if (!value) {
      return (
        <DashboardTypography variant="body2" vibe="text-neutral-quiet">
          {t("dashboard", "ContactFiltering.filterByPlaceholder")}
        </DashboardTypography>
      );
    }

    const selectedValue = value as string;
    // Fall back to the registry so a hidden-but-active filter (e.g. a legacy filter kept for saved lists) keeps its label/icon.
    const filterConfig = availableOptions.find((c) => c.id === selectedValue) ?? registry.get(selectedValue);

    if (!filterConfig) {
      return selectedValue;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <DashboardIcon size="small">{filterConfig.icon as IconElement}</DashboardIcon>
        <span>{filterConfig.label}</span>
      </Box>
    );
  };

  return (
    <>
      {!isSubMenuOpen && !selectedGroup && (
        <DashboardSelect
          value={filterConfigId}
          onChange={(event) => {
            const value = event.target.value as string;
            if (groupedFilters[value]) {
              handleGroupClick(value);
              setIsMainMenuOpen(false);
            } else {
              onCategorySelected?.({ category: value, opensSubmenu: false });
              handleTypeChange(value);
              setIsMainMenuOpen(false);
            }
          }}
          open={isMainMenuOpen}
          onOpen={() => {
            setIsMainMenuOpen(true);
            onDropdownOpened?.();
          }}
          onClose={() => {
            setIsMainMenuOpen(false);
          }}
          placeholder={t("dashboard", "ContactFiltering.filterByPlaceholder")}
          size="medium"
          sx={{ width: "100%" }}
          data-test="filter-type-select"
          renderValue={renderValue}
          displayEmpty
          MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
        >
          {orderedMenuItems.map((item) => {
            if (item.type === "group") {
              return (
                <DashboardMenuItem
                  key={item.groupName}
                  value={item.groupName}
                  startIcon={item.icon as IconElement}
                  endIcon={<ChevronRight />}
                >
                  {getGroupLabel(item.groupName!)}
                </DashboardMenuItem>
              );
            }

            if (!item.config) {
              return null;
            }

            return (
              <DashboardMenuItem
                key={item.config.id}
                value={item.config.id}
                startIcon={item.config.icon as IconElement}
              >
                {item.config.label}
              </DashboardMenuItem>
            );
          })}
          {Object.values(groupedFilters)
            .flat()
            .map((filterConfig) => (
              <DashboardMenuItem
                key={filterConfig.id}
                value={filterConfig.id}
                startIcon={filterConfig.icon as IconElement}
                sx={{ display: "none" }}
              >
                {filterConfig.label}
              </DashboardMenuItem>
            ))}
        </DashboardSelect>
      )}

      {isSubMenuOpen && selectedGroup && (
        <DashboardSelect
          onChange={(event) => {
            const value = event.target.value as string;
            if (value === "_back") {
              handleBackClick();
            } else {
              onSubmenuItemSelected?.({ category: selectedGroup, filterType: value });
              handleTypeChange(value);
            }
          }}
          open={true}
          placeholder={t("dashboard", "ContactFiltering.filterByPlaceholder")}
          size="medium"
          sx={{ width: "100%" }}
          data-test="filter-type-select-submenu"
          renderValue={renderValue}
          displayEmpty
          MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
        >
          <DashboardMenuItem value="_back" startIcon={<ChevronLeft />}>
            {t("dashboard", "ContactFiltering.back")}
          </DashboardMenuItem>
          {groupedFilters[selectedGroup]?.map((filterConfig) => (
            <DashboardMenuItem
              key={filterConfig.id}
              value={filterConfig.id}
              startIcon={filterConfig.icon as IconElement}
            >
              {filterConfig.label}
            </DashboardMenuItem>
          ))}
        </DashboardSelect>
      )}
    </>
  );
};
